/**
 * Quién puede hacer qué dentro de un proyecto.
 *
 * Vive aquí, y no en el panel ni en el worker, porque los dos tienen que estar
 * de acuerdo y por motivos distintos: el panel lo usa para decidir qué entradas
 * del menú enseña, y el worker para decidir si acepta la petición. Si cada uno
 * llevara su copia, el día que diverjan el menú prometería una sección que el
 * servidor rechaza —o peor, escondería una que sí deja pasar, que es una reja
 * pintada—.
 *
 * ── La forma del diseño ──────────────────────────────────────────────────────
 *
 * Los roles NO se comparan nunca por nombre. Un rol es un nombre y un conjunto
 * de CAPACIDADES, y todo lo que decide se decide preguntando por la capacidad:
 * `can(role, 'pages.write')`. Comparar por nombre —`if (role === 'admin')`— es
 * lo que hace que añadir un rol obligue a revisar cincuenta condicionales y que
 * siempre quede una sin tocar.
 *
 * ── Por qué el catálogo es cerrado ───────────────────────────────────────────
 *
 * Son presets fijos, no una matriz que el administrador marca casilla a casilla.
 * Una matriz abierta se configura mal: es fácil dejar a alguien con acceso a los
 * pedidos pero no a los contactos que los hicieron, y esas combinaciones no las
 * pidió nadie —salen de que la pantalla las permitía—. Cinco roles con nombre
 * cubren los casos reales y ninguno de ellos es un estado incoherente.
 */
/** Todo lo que se puede leer, que es lo que tiene quien sólo mira. */
const READ_ONLY = [
    'project.read',
    'pages.read',
    'cms.read',
    'assets.read',
    'forms.read',
    'contacts.read',
    'bookings.read',
    'commerce.read',
    'analytics.read',
    'backups.read',
];
/** Editar el sitio y lo que entra por él, sin tocar dinero ni ajustes. */
const EDITOR = [
    'project.read',
    'notes.write',
    'pages.read', 'pages.write',
    'cms.read', 'cms.write',
    'assets.read', 'assets.write',
    'forms.read', 'forms.write',
    'contacts.read', 'contacts.write',
    'bookings.read', 'bookings.write',
    'analytics.read',
    'backups.read', 'backups.create',
    'ai.use',
    /* La configuración del sitio entra en «editar el sitio»: el nombre, la zona
       horaria, las políticas y los ajustes de publicación son parte de dejarlo
       listo, no de administrarlo. Lo que NO entra es borrarlo ni traspasarlo, y
       eso no cuelga de aquí sino de `project.danger`, que sólo tiene el dueño. */
    'project.settings',
];
/** Todo menos lo que sólo puede hacer quien tiene el proyecto a su nombre. */
const ADMIN = [
    ...EDITOR,
    'domains.manage',
    'integrations.manage',
    'backups.manage',
    'commerce.read', 'commerce.write',
    'team.manage',
];
export const PROJECT_ROLES = {
    admin: {
        id: 'admin',
        labelKey: 'role.admin.label',
        summaryKey: 'role.admin.summary',
        capabilities: ADMIN,
        tone: 'cobalt',
    },
    editor: {
        id: 'editor',
        labelKey: 'role.editor.label',
        summaryKey: 'role.editor.summary',
        capabilities: EDITOR,
        tone: 'green',
    },
    design: {
        id: 'design',
        labelKey: 'role.design.label',
        summaryKey: 'role.design.summary',
        capabilities: [
            'project.read',
            'notes.write',
            'pages.read', 'pages.write',
            'assets.read', 'assets.write',
            'cms.read',
            'analytics.read',
            'ai.use',
        ],
        tone: 'violet',
    },
    content: {
        id: 'content',
        labelKey: 'role.content.label',
        summaryKey: 'role.content.summary',
        capabilities: [
            'project.read',
            'notes.write',
            'cms.read', 'cms.write',
            'forms.read', 'forms.write',
            'contacts.read', 'contacts.write',
            'assets.read', 'assets.write',
            'pages.read',
            'ai.use',
        ],
        tone: 'amber',
    },
    finance: {
        id: 'finance',
        labelKey: 'role.finance.label',
        summaryKey: 'role.finance.summary',
        capabilities: [
            'project.read',
            'notes.write',
            'commerce.read', 'commerce.write',
            'bookings.read', 'bookings.write',
            'contacts.read',
            'analytics.read',
        ],
        tone: 'teal',
    },
    viewer: {
        id: 'viewer',
        labelKey: 'role.viewer.label',
        summaryKey: 'role.viewer.summary',
        capabilities: READ_ONLY,
        tone: 'slate',
    },
};
/** En el orden en que se enseñan: de más acceso a menos. */
export const PROJECT_ROLE_ORDER = [
    'admin', 'editor', 'design', 'content', 'finance', 'viewer',
];
export function isProjectRole(value) {
    return typeof value === 'string' && value in PROJECT_ROLES;
}
/**
 * La pregunta que decide todo.
 *
 * El dueño pasa siempre y sin consultar tabla: es el único que tiene
 * `project.danger` y `project.billing`, y darle una fila en `project_members`
 * sólo para que se le pudiera preguntar por ellas sería duplicar el dueño en dos
 * sitios que pueden discrepar.
 */
export function can(role, capability) {
    if (role === 'owner')
        return true;
    return PROJECT_ROLES[role].capabilities.includes(capability);
}
/**
 * La clave de la etiqueta para pantalla, con el dueño incluido.
 *
 * Devuelve la clave y no el texto por lo mismo que `labelKey`: quien pinta sabe
 * en qué idioma, y este paquete no.
 */
export function roleLabelKey(role) {
    return role === 'owner' ? 'role.owner.label' : PROJECT_ROLES[role].labelKey;
}
export function roleTone(role) {
    return role === 'owner' ? 'gold' : PROJECT_ROLES[role].tone;
}
/**
 * Qué capacidad abre cada sección del panel.
 *
 * Es lo que traduce «este rol» a «este menú», y está aquí y no en el panel para
 * que la sección y la ruta que la sirve pidan lo mismo. Las que faltan —las de
 * staff de véltica, y las globales que no cuelgan de un proyecto— no se filtran
 * por rol porque no son del proyecto.
 */
export const SECTION_CAPABILITY = {
    overview: 'project.read',
    pages: 'pages.read',
    cms: 'cms.read',
    contacts: 'contacts.read',
    forms: 'forms.read',
    analytics: 'analytics.read',
    bookings: 'bookings.read',
    payments: 'commerce.read',
    /* El catálogo con el filtro puesto: el mismo permiso que el catálogo. Sin esto
       `canOpenSection` la daba por libre —no estaba en el mapa— y un invitado de
       contenido veía las ofertas del proyecto en su menú. */
    digitalProducts: 'commerce.read',
    /* Y su gemela, el catálogo filtrado a lo que se hace en vez de a lo que se
       descarga: el mismo permiso, porque es la misma lista. */
    services: 'commerce.read',
    orders: 'commerce.read',
    coupons: 'commerce.read',
    collections: 'commerce.read',
    payouts: 'commerce.read',
    salesAnalytics: 'commerce.read',
    bookingsAnalytics: 'bookings.read',
    stripe: 'commerce.write',
    team: 'project.read',
    omnisend: 'integrations.manage',
    openTable: 'project.settings',
    calendars: 'bookings.write',
    config: 'project.settings',
    general: 'project.settings',
    business: 'project.settings',
    region: 'project.settings',
    policies: 'project.settings',
    publish: 'project.settings',
    /* Entregar el sitio no es un ajuste: es dejar de tenerlo. Va con `danger`, el
       mismo permiso que borrar, y por eso sólo lo ve el dueño. */
    transfer: 'project.danger',
    googleAnalytics: 'project.settings',
    /* La sección se ABRE con leer; sus botones piden `backups.manage` aparte. */
    backups: 'backups.read',
    /* `plans` no está: la membresía es de la PERSONA y no del sitio, así que dejó
       de ser una sección del proyecto y vive en el menú de usuario. Aquí sólo se
       enumera lo que un rol puede abrir DENTRO de un proyecto ajeno, y el plan de
       uno no es eso — cada quien ve el suyo, entre en el proyecto de quien entre. */
    usage: 'project.billing',
};
/**
 * El nombre de cada sección en el menú.
 *
 * Aquí y no en el panel porque lo usan dos pantallas —el selector de rol al
 * invitar y la pantalla de quien recibe la invitación— y las dos tienen que
 * decir lo mismo. Sólo están las que se enumeran al explicar un rol: las de
 * staff y las globales no son del proyecto y no se prometen.
 */
/* Claves, no nombres: lo mismo que `labelKey`. Este paquete lo comparten cuatro
   sitios que pintan en idiomas distintos, y quien pinta las resuelve. */
export const SECTION_LABEL = {
    overview: 'section.overview',
    pages: 'section.pages',
    cms: 'section.cms',
    contacts: 'section.contacts',
    forms: 'section.forms',
    analytics: 'section.analytics',
    bookings: 'section.bookings',
    payments: 'section.payments',
    orders: 'section.orders',
    payouts: 'section.payouts',
    salesAnalytics: 'section.salesAnalytics',
    bookingsAnalytics: 'section.bookingsAnalytics',
    stripe: 'section.stripe',
    backups: 'section.backups',
    general: 'section.general',
    team: 'section.team',
};
/**
 * A qué llega un rol, en nombres de menú.
 *
 * Se deriva de `SECTION_CAPABILITY` en vez de escribirse a mano: la lista que se
 * le enseña a quien invita y la reja que aplica el servidor salen así de la
 * misma fuente y no pueden acabar diciendo cosas distintas.
 */
export function sectionsForRole(role) {
    /* Devuelve CLAVES; quien las pinta les pasa su traductor. */
    return Object.entries(SECTION_CAPABILITY).flatMap(([section, capability]) => {
        const label = SECTION_LABEL[section];
        return label && can(role, capability) ? [label] : [];
    });
}
/**
 * Todas las secciones del proyecto, marcadas según las abra el rol o no.
 *
 * Hermana de `sectionsForRole`, que sólo devuelve las que sí. Ésta hace falta
 * para poder ENSEÑAR lo que un rol no alcanza: una lista de cinco cosas
 * permitidas no dice si las otras nueve existen, y quien reparte permisos
 * necesita ver justo eso — es la diferencia entre «esto abre lo que necesito» y
 * «esto además NO abre los pedidos», que es la comprobación que evita dar
 * administrador por si acaso.
 */
export function sectionAccessForRole(role) {
    return Object.entries(SECTION_CAPABILITY).flatMap(([section, capability]) => {
        const label = SECTION_LABEL[section];
        return label ? [{ label, allowed: can(role, capability) }] : [];
    });
}
/** Si un rol puede abrir una sección. Lo desconocido no se filtra. */
export function canOpenSection(role, section) {
    const capability = SECTION_CAPABILITY[section];
    if (!capability)
        return true;
    return can(role, capability);
}
//# sourceMappingURL=roles.js.map